import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Navigate, Route, Routes, useSearchParams } from 'react-router'

import { AdminShell } from './admin/AdminShell'
import { AuthProvider } from './admin/AuthContext'
import { ProtectedRoute } from './admin/ProtectedRoute'
import { SelectedHospitalProvider } from './admin/SelectedHospitalContext'
import { AdmitBookingPage } from './admin/pages/AdmitBookingPage'
import { AdminHomePage } from './admin/pages/AdminHomePage'
import { AppointmentsPage } from './admin/pages/AppointmentsPage'
import { AuditLogPage } from './admin/pages/AuditLogPage'
import { EditBedsPage } from './admin/pages/EditBedsPage'
import { EmergencyContactsPage } from './admin/pages/EmergencyContactsPage'
import { TransportBookingsPage } from './admin/pages/TransportBookingsPage'
import { TransportSettingsPage } from './admin/pages/TransportSettingsPage'
import { EmergencyCoordinationPage } from './admin/pages/EmergencyCoordinationPage'
import { EmergencyRequestsPage } from './admin/pages/EmergencyRequestsPage'
import { DueForDeletionPage } from './admin/pages/DueForDeletionPage'
import { EnquiriesPage } from './admin/pages/EnquiriesPage'
import { LeadsPage } from './admin/pages/LeadsPage'
import { LoginPage } from './admin/pages/LoginPage'
import { MedicationCurationPage } from './admin/pages/MedicationCurationPage'
import { MedicationRequestsPage } from './admin/pages/MedicationRequestsPage'
import { ProfilePage } from './admin/pages/ProfilePage'
import { ServiceRequestsPage } from './admin/pages/ServiceRequestsPage'
import { Foundations } from './components/Foundations'
import { ErrorFallback } from './components/ErrorFallback'
import { Sentry } from './lib/sentry'
import { BedTypePicker } from './pages/BedTypePicker'
import { BookBed } from './pages/BookBed'
import { CheckoutReturn } from './pages/CheckoutReturn'
import { Contact } from './pages/Contact'
import { DataProtection } from './pages/DataProtection'
import { DiagnosticsSearch } from './pages/DiagnosticsSearch'
import { EmergencyHospitalDetail } from './pages/emergency-help/EmergencyHospitalDetail'
import { EmergencyHospitals } from './pages/emergency-help/EmergencyHospitals'
import { EmergencyRequest } from './pages/emergency-help/EmergencyRequest'
import { EmergencyStart } from './pages/emergency-help/EmergencyStart'
import { LegacyEmergencyHelp } from './pages/emergency-help/LegacyRedirect'
import { Transport } from './pages/Transport'
import { TransportBookingPage } from './pages/transport/BookingPage'
import { TransportReceipt } from './pages/transport/Receipt'
import { HospitalResults } from './pages/HospitalResults'
import { LocalHome } from './pages/LocalHome'
import { LocalProfile } from './pages/LocalProfile'
import { MedicineOffer } from './pages/MedicineOffer'
import { MedicineRequest } from './pages/MedicineRequest'
import { Medicines } from './pages/Medicines'
import { Privacy } from './pages/Privacy'
import { RequestService } from './pages/RequestService'
import { Terms } from './pages/Terms'
import { AppointmentRequest } from './pages/AppointmentRequest'
import { AppointmentRequestSent } from './pages/AppointmentRequestSent'
import { MyAppointments } from './pages/MyAppointments'
import { SpecialistProfile } from './pages/SpecialistProfile'
import { SpecialistSearch } from './pages/SpecialistSearch'
import { TourismEnquiry } from './pages/TourismEnquiry'
import { TourismHome } from './pages/TourismHome'
import { TourismProfile } from './pages/TourismProfile'
import { useLiveBeds } from './lib/useLiveBeds'
import { ViewProvider, useView } from './views/ViewContext'

/** Subscribes to the live bed-count stream while the Local interface is active
 * (M5-T03). Rendered alongside the routes so updates persist across the home
 * and profile; tears down on the Tourism view. Renders nothing. */
function LiveBedsBridge() {
  const { view } = useView()
  useLiveBeds(view === 'local')
  return null
}

function Body() {
  const { view } = useView()
  const [params] = useSearchParams()

  // Keep Sentry tagged with the active view so errors break down by
  // interface in the Sentry UI.
  useEffect(() => {
    Sentry.setTag('view', view)
  }, [view])

  // M34-T04 (ADR-0063): an old "hospitals with an ambulance" link lands on
  // Transport, not on a results screen headed "Ambulance". The backend filter
  // `row1=ambulance` is unchanged; only the customer landing moved. Any other
  // filters on such a link are dropped with it (accepted in the ticket). It
  // runs before the view check, so the rule holds in either view, as
  // `/ambulance` does.
  if (params.getAll('row1').includes('ambulance'))
    return <Navigate to="/transport" replace />
  if (view !== 'local') return <TourismHome />
  // Local `/`: the Option B tiles home by default (M12-T01, ADR-0026); the
  // hospital-results screen when a filter/search is present in the URL — so
  // tiles can deep-link with a chip pre-applied and `/?row1=…` stays
  // back-compatible.
  const showResults =
    params.has('row1') ||
    params.has('row2') ||
    params.has('q') ||
    params.has('bedtype')
  return showResults ? <HospitalResults /> : <LocalHome />
}

// One client for the app's data fetching (cache, retries, invalidation),
// shared by the public bed-finder and the admin portal.
const queryClient = new QueryClient()

/** Public Local / Tourism app, mounted at `/`. Owns its own ViewProvider and
 * is NOT wrapped by AuthProvider; the Local home fetches via React Query. */
function PublicApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <ViewProvider>
        <LiveBedsBridge />
        <Routes>
          <Route index element={<Body />} />
          <Route path="hospital/:slug" element={<LocalProfile />} />
          <Route path="book/:slug" element={<BookBed />} />
          <Route path="checkout/return" element={<CheckoutReturn />} />
          <Route path="beds" element={<BedTypePicker />} />
          {/* Emergency Help v2 (ADR-0064), public at /emergency since the
              switch-over (M36-T11). The old call-ahead flow is retired. */}
          <Route path="emergency" element={<EmergencyStart />} />
          <Route path="emergency/hospitals" element={<EmergencyHospitals />} />
          <Route
            path="emergency/hospitals/:slug"
            element={<EmergencyHospitalDetail />}
          />
          {/* Screen 4, only while a coordinator is on duty (M36-T08). */}
          <Route path="emergency/request" element={<EmergencyRequest />} />
          {/* Where v2 lived while hidden (M35): every address redirects to
              its /emergency twin, query string kept. */}
          <Route path="emergency-help/*" element={<LegacyEmergencyHelp />} />
          {/* An unknown address under /emergency lands on screen 1 and its
              112, never on a blank page. */}
          <Route
            path="emergency/*"
            element={<Navigate to="/emergency" replace />}
          />
          {/* M34-T04: the Ambulance page is retired; old links forward. */}
          <Route
            path="ambulance"
            element={<Navigate to="/transport" replace />}
          />
          <Route path="transport" element={<Transport />} />
          {/* M40-T10: the private booking page and its receipt; the key is
              in the URL fragment, never the path. */}
          <Route
            path="transport/booking/:reference"
            element={<TransportBookingPage />}
          />
          <Route
            path="transport/booking/:reference/receipt"
            element={<TransportReceipt />}
          />
          <Route path="medicines" element={<Medicines />} />
          <Route path="medicines/request/:slug" element={<MedicineRequest />} />
          <Route
            path="medicines/offer/:reference"
            element={<MedicineOffer />}
          />
          <Route path="specialists" element={<SpecialistSearch />} />
          <Route path="specialists/:slug" element={<SpecialistProfile />} />
          <Route
            path="specialists/:specialistSlug/request/:hospitalSlug"
            element={<AppointmentRequest />}
          />
          <Route path="appointments" element={<MyAppointments />} />
          <Route
            path="appointments/sent"
            element={<AppointmentRequestSent />}
          />
          <Route path="diagnostics" element={<DiagnosticsSearch />} />
          <Route
            path="service/:procedureSlug/:hospitalSlug"
            element={<RequestService />}
          />
          <Route path="tourism/hospital/:slug" element={<TourismProfile />} />
          <Route path="tourism/enquiry" element={<TourismEnquiry />} />

          {/* M17-T17 — the surfaces the footer had to drop. Routes are kept
              boring and stable because they get linked from outside the app. */}
          <Route path="privacy" element={<Privacy />} />
          <Route path="terms" element={<Terms />} />
          <Route path="data-protection" element={<DataProtection />} />
          <Route path="contact" element={<Contact />} />
        </Routes>
        {import.meta.env.DEV && <Foundations />}
      </ViewProvider>
    </QueryClientProvider>
  )
}

/** Admin portal under `/admin`. Wrapped in QueryClientProvider + AuthProvider;
 * login is public, everything else is behind <ProtectedRoute> + <AdminShell>. */
function AdminApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SelectedHospitalProvider>
          <Routes>
            <Route path="login" element={<LoginPage />} />
            <Route element={<ProtectedRoute />}>
              <Route element={<AdminShell />}>
                <Route index element={<AdminHomePage />} />
                <Route path="beds" element={<EditBedsPage />} />
                <Route path="admit" element={<AdmitBookingPage />} />
                <Route
                  path="service-requests"
                  element={<ServiceRequestsPage />}
                />
                <Route path="appointments" element={<AppointmentsPage />} />
                <Route
                  path="medication-requests"
                  element={<MedicationRequestsPage />}
                />
                <Route
                  path="medication-curation"
                  element={<MedicationCurationPage />}
                />
                <Route path="enquiries" element={<EnquiriesPage />} />
                <Route path="leads" element={<LeadsPage />} />
                <Route
                  path="emergency-requests"
                  element={<EmergencyRequestsPage />}
                />
                <Route
                  path="emergency-contacts"
                  element={<EmergencyContactsPage />}
                />
                {/* M38-T07: Medcor Transport's fares, areas, zones, capacity. */}
                <Route
                  path="transport-settings"
                  element={<TransportSettingsPage />}
                />
                {/* M40-T12: the transport desk; `?view=` picks the list. */}
                <Route
                  path="transport-bookings"
                  element={<TransportBookingsPage />}
                />
                <Route path="audit-log" element={<AuditLogPage />} />
                {/* M37-T02: what the 90-day deletion clears next. */}
                <Route
                  path="due-for-deletion"
                  element={<DueForDeletionPage />}
                />
                <Route
                  path="emergency-coordination"
                  element={<EmergencyCoordinationPage />}
                />
                {/* Reached from the user card menu, deliberately NOT the
                    sidebar: the sidebar lists queues of work. */}
                <Route path="profile" element={<ProfilePage />} />
              </Route>
            </Route>
          </Routes>
        </SelectedHospitalProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}
    >
      <Routes>
        <Route path="/admin/*" element={<AdminApp />} />
        <Route path="/*" element={<PublicApp />} />
      </Routes>
    </Sentry.ErrorBoundary>
  )
}

export default App
